/**
 * Claude 프로젝트 지식 파일 (AI_WORKFLOW_PLAN_v1 §2-3).
 * 공통 5종은 모든 내담자 프로젝트에 같은 파일, 사례현황은 내담자마다 앱이 만든다.
 * 내용을 바꾸면 KNOWLEDGE_VERSION을 올린다 (프로젝트의 파일을 다시 올려야 함을 화면에 표시).
 */
import { DOC_DRAFT_SPECS } from '../docDraft';
import { BEGIN, CLAUDE_PROJECT_INSTRUCTION, END, SESSION_OUTPUT_SPEC } from '../prompts';
import { CHANGE_POINTS_MD } from './changePoints';
import { THEORIES_MD } from './theories';
import type { CaseReportInput } from '../../workflow/documents';
import { goalStatusLabel } from '../../workflow/documents';
import { FORMULATION_ITEM_FIELDS, FORMULATION_LABELS, FORMULATION_LIST_FIELDS } from '../../workflow/formulation';

export const KNOWLEDGE_VERSION = 'v1';

const DOC_TITLES: Record<string, string> = {
  session_record: '상담기록지',
  case_report: '상담사례 보고서 (장신상담센터 기본 양식)',
  termination_report: '상담 종결 보고서',
  verbatim: '축어록',
  dialogue: '대화록(Verbatim) — 수업용 목회상담 양식',
};

const OUTPUT_MD = `# 01 출력 형식 (${KNOWLEDGE_VERSION})

앱(SDCare)은 Claude의 응답에서 아래 표식 사이의 JSON 하나만 읽는다. 표식 밖의 설명은 버려진다.
코드 블록 하나 안에 출력하고, 아티팩트로 만들지 않는다.

\`\`\`
${BEGIN}
{ ...JSON... }
${END}
\`\`\`

## 1. 회기 분석 (매 회기, SESSION_ANALYSIS_V2)

${SESSION_OUTPUT_SPEC}

## 2. 문서 초안 (문서 만들 때, DOC_DRAFT_V1)

\`\`\`
{"formatVersion": "DOC_DRAFT_V1", "fields": {"항목키": "그 칸에 들어갈 내용"}, "cautions": ["주의할 점"]}
\`\`\`
- 앱이 요청문에 적은 항목키만 쓴다. 각 내용은 그 칸에 바로 들어갈 문장.
- 보고서 문체(~함, ~임). 근거가 약하면 "추정", 자료가 없으면 "자료 부족 — 다음 회기에 확인할 것: …".

## 3. 응답이 길 때
- 문장을 줄이되 항목은 빼지 않는다.
- 끊겼다면 앱이 "빠진 항목만 다시" 요청문을 준다. 그때는 빠진 항목만 같은 형식으로 낸다.
`;

const FORMS_MD = `# 04 문서 양식 규격 (${KNOWLEDGE_VERSION})

모든 문서 초안은 상담자가 확인·수정한다. AI가 쓴 칸에는 앱이 "[AI 초안 — 확인 필요]"를 붙인다.
공통 문체: 보고서체(~함, ~임), 내담자 진술은 "~라고 보고함", 관찰은 "~로 관찰됨", 가설은 "~일 가능성". 사람 이름 대신 관계(어머니, 팀장 등).

${Object.entries(DOC_DRAFT_SPECS)
  .map(
    ([type, specs]) =>
      `## ${DOC_TITLES[type] ?? type}\n${specs
        .map((s) => `- **${s.mode === 'fill' ? s.heading.replace(/^#+\s*/, '') : s.label}** (키: ${s.key}) — ${s.ask}`)
        .join('\n')}`,
  )
  .join('\n\n')}

## 상담사례 보고서 구성 (센터 양식 순서)
I. 주요 호소 문제 (내담자가 직접 표현한 그대로 “ ”) / II. 임상 자료 1~9 (인적 사항, 내담 경위, 인상·행동 관찰, 심리검사, 가족 관계·가계도, 사회적 관계, 발달사, 강점·약점, 신앙 이해) / III. 사례개념화 (①주호소·문제행동 ②유발요인(신앙 요인 포함) ③촉발·유지 과정, 상담 이론에 근거) / IV. 상담자 자신에 대한 이해 / V. 상담 목표·전략·개입 절차 (합의 목표·임상 목표, 전략, 회기 표, 회기 요약, 축어록) / VI. 상담 평가 / VII. 수퍼비전 받고 싶은 점 / VIII. 신학적 성찰.

## 대화록(Verbatim) 구성 (수업 양식)
머리 표(면담 일·일련번호·목회상담자·면담 시간·내담자 가명·만남 회수·나이/성별·대화 주제·직업/교육·종교) / I. 사실 파악과 준비 / II. 관찰 / III. 면담 (상1·내1 짝 번호) / IV. 분석 / V. 신학적 의미 / VI. 앞으로의 계획.
`;

const NOTATION_MD = `# 05 축어록 표기와 선정 (${KNOWLEDGE_VERSION})

## 표기
- 기본: **상1, 내1** (상 = 상담자, 내 = 내담자). 국내 상담 수련·사례 발표에서 가장 흔한 방식.
- 앱이 문서를 보여줄 때 P1/C1(목회상담), T1/C1, Co1/Cl1로 바꿀 수 있다. 분석·초안에서는 항상 상/내 또는 segmentId로 적는다.
- 축어록(사례보고서·축어록 문서): 화자별 순번 (상1, 상2 … / 내1, 내2 …).
- 대화록(수업 양식): 짝 번호 (상1 → 내1, 상2 → 내2). 같은 사람이 이어서 말한 것은 한 문단.
- 화자가 불확실한 줄은 "?(확인 필요)"로 둔다.

## 구간 선정
- 길이: 10~15분 (사례보고서·대화록 제출 기준).
- 기준: 03 변화 지점 판단기준. 변화 전 맥락 → 계기(상담자 개입) → 변화 후가 모두 들어가게.
- 선정 이유: "어떤 변화가, 어떤 개입 뒤에, 왜 사례개념화에 중요한가"를 2~3문장으로.
- 수퍼비전 질문: 그 구간의 상담자 개입에 대해 구체적으로 (발언 번호 포함).
`;

export type KnowledgeFile = { name: string; content: string };

/** 모든 내담자 프로젝트에 올리는 파일 하나 (아래 5개 내용을 합친 것) */
export function commonKnowledgeFile(): KnowledgeFile {
  return {
    name: `SDCare_상담지식_${KNOWLEDGE_VERSION}.md`,
    content: [
      `# SDCare 상담 지식 (${KNOWLEDGE_VERSION}) — 모든 내담자 프로젝트 공통`,
      '이 파일은 5부분으로 되어 있다: 01 출력 형식 / 02 이론과 기법 기준표 / 03 변화 지점 판단기준 / 04 문서 양식 규격 / 05 축어록 표기와 선정.',
      ...COMMON_KNOWLEDGE.map((f) => f.content),
    ].join('\n\n---\n\n'),
  };
}

export const COMMON_KNOWLEDGE: KnowledgeFile[] = [
  { name: `01_출력형식_${KNOWLEDGE_VERSION}.md`, content: OUTPUT_MD },
  { name: `02_이론과_기법_기준표_${KNOWLEDGE_VERSION}.md`, content: THEORIES_MD },
  { name: `03_변화지점_판단기준_${KNOWLEDGE_VERSION}.md`, content: CHANGE_POINTS_MD },
  { name: `04_문서양식_규격_${KNOWLEDGE_VERSION}.md`, content: FORMS_MD },
  { name: `05_축어록_표기와_선정_${KNOWLEDGE_VERSION}.md`, content: NOTATION_MD },
];

const list = (xs: (string | undefined)[]) => xs.filter(Boolean).map((x) => `- ${x}`).join('\n') || '- (없음)';

/** 내담자별 사례현황 파일 (식별정보 없음: 이니셜 코드·나이·성별·직업 등만) */
export function buildCaseStatusFile(i: CaseReportInput & { ageText?: string }): KnowledgeFile {
  const c = i.client;
  const done = [...i.sessions].sort((a, b) => a.sessionNumber - b.sessionNumber);
  const journals = new Map(i.journals.map((j) => [j.sessionId, j]));
  const analyses = new Map(i.analyses.map((a) => [a.sessionId, a]));
  const f = i.formulation;
  const out: string[] = [
    `# ${c.clientCode} 사례현황 (${i.asOf.slice(0, 10)} 기준)`,
    '',
    '이 파일은 앱이 만든 요약이다. 회기 자료(패킷)의 [현재 사례 상태]가 더 최신이면 패킷을 따른다.',
    '',
    '## 기본 정보',
    list([
      `코드: ${c.clientCode}`,
      i.ageText && `나이: ${i.ageText}`,
      c.gender && `성별: ${c.gender}`,
      c.occupationRole && `직업: ${c.occupationRole}`,
      c.education && `교육: ${c.education}`,
      c.religion && `종교: ${c.religion}`,
      c.priorCounselingHistory && `이전 상담: ${c.priorCounselingHistory}`,
      c.familyStructure && `가족: ${c.familyStructure.replace(/\n/g, ' / ')}`,
      c.faithSpiritualContext && `신앙: ${c.faithSpiritualContext.replace(/\n/g, ' / ')}`,
      c.maxSessions ? `최대 상담 가능 회기: ${c.maxSessions}회` : undefined,
    ]),
    '',
    '## 주호소',
    list(i.intake?.presentingProblems ?? []),
    '',
    '## 상담자가 이끌어 가고자 하는 방향',
    c.counselingDirection?.trim() || '(적지 않음)',
    '',
    '## 상담 목표',
    list(i.goals.filter((g) => !g.deletedAt).map((g) => `${g.title} (${g.agreedWithClient ? '합의' : '임상적'}, 현재 ${goalStatusLabel(g.status)})`)),
  ];
  if (f) {
    out.push('', `## 현재 사례개념화 (v${f.version})`);
    for (const k of FORMULATION_LIST_FIELDS) if (f[k]?.length) out.push(`${FORMULATION_LABELS[k]}:`, list(f[k]!));
    for (const k of FORMULATION_ITEM_FIELDS) {
      const items = (f[k] ?? []).filter((x) => !(f.rejectedHypotheses ?? []).includes(x.id));
      if (items.length) out.push(`${FORMULATION_LABELS[k]}:`, list(items.map((x) => `${x.status === 'hypothesis' ? '(가설) ' : ''}${x.text}`)));
    }
  }
  if (i.diagnosis?.considerations.length)
    out.push('', '## 진단적 고려 (공식 진단 아님)', list(i.diagnosis.considerations.map((d) => `${d.diagnosisName}: ${d.status}`)));
  out.push(
    '',
    '## 회기별 요약',
    list(
      done.map((s) => {
        const a = analyses.get(s.id);
        const j = journals.get(s.id);
        return `S${s.sessionNumber} (${s.sessionDate}): ${a?.sessionSummary ?? j?.importantIssue ?? '요약 없음'}`;
      }),
    ),
  );
  const quotes = i.evidence.filter((e) => e.evidenceType === 'CLIENT_REPORT' && e.verified && !e.deletedAt).slice(-20);
  if (quotes.length) out.push('', '## 확인된 내담자 진술', list(quotes.map((q) => `(S${q.sourceSession ?? '?'}) ${q.text}`)));
  return { name: `${c.clientCode}_사례현황.md`, content: out.join('\n') + '\n' };
}

/**
 * Claude 프로젝트에 넣는 것 (선생님 화면 기준):
 * - 프로젝트 지침 칸(약 16,000바이트 한도): 지침 + 변화 지점 판단기준 + 축어록 표기 — 원문 그대로
 * - 컨텍스트(새 프로젝트 창): 이론과 기법 기준표 파일 하나 — 원문 그대로
 * 출력 형식과 문서 칸 설명은 앱이 보내는 자료에 매번 들어 있으므로 넣지 않는다.
 */
export const PROJECT_INSTRUCTION_LIMIT_BYTES = 15_500;
export const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;

export function projectInstructionFull(): string {
  return [
    CLAUDE_PROJECT_INSTRUCTION.replace(/\(지식 파일의 기준표 우선\)/g, '').replace(/지식 파일의 기준표는/g, '프로젝트 컨텍스트의 「이론과 기법 기준표」는').replace(/\(지식 파일의 변화지점 판단기준\)/, '(아래 부록 B)').replace('프로젝트 지식 파일은', '프로젝트 컨텍스트 파일·메모리는'),
    '',
    `===== 부록 (SDCare 기준 ${KNOWLEDGE_VERSION}) — 이론과 기법 기준표는 프로젝트 컨텍스트 파일, 출력 형식과 문서 칸 설명은 앱이 보내는 자료에 들어 있다 =====`,
    '',
    '[부록 B]',
    CHANGE_POINTS_MD,
    '[부록 C]',
    NOTATION_MD,
  ].join('\n');
}

/** 컨텍스트에 올리는 파일: 이론과 기법 기준표 (원문 그대로) */
export function theoryContextFile(): KnowledgeFile {
  return { name: `SDCare_이론과기법_기준표_${KNOWLEDGE_VERSION}.md`, content: THEORIES_MD };
}
